// Site-wide WebMCP tools: available on every page.

import { CORE_PAGES, GUIDE_PAGES, SERVICE_PAGES, SITE } from "@/lib/site-content";
import { errorResult, jsonResult, textResult } from "../types";
import type { WebMcpTool } from "../types";

const ALL_PAGES = [...CORE_PAGES, ...SERVICE_PAGES, ...GUIDE_PAGES];

export type SiteToolDeps = {
  /** In-app navigation (keeps the SPA session and any registered tools warm). */
  navigate: (to: string) => void;
  /** Current pathname, read at call time. */
  currentPath: () => string;
};

function readableText(): string {
  const main = document.querySelector("main") ?? document.body;
  const text = (main.innerText || "").replace(/\n{3,}/g, "\n\n").trim();
  return text.length > 12000 ? `${text.slice(0, 12000)}\n…[truncated]` : text;
}

export function createSiteTools(deps: SiteToolDeps): WebMcpTool[] {
  return [
    {
      name: "vibing_read_current_page",
      title: "Read the current page",
      description:
        "Return the visible text, title and URL of the page the user is looking at right now. Use this before answering questions about 'this page'.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, idempotentHint: false, untrustedContentHint: true },
      execute: () =>
        jsonResult(
          {
            url: window.location.href,
            path: deps.currentPath(),
            title: document.title,
            headings: [...document.querySelectorAll("h1, h2")]
              .slice(0, 40)
              .map((h) => (h.textContent ?? "").trim())
              .filter(Boolean),
            text: readableText(),
          },
          `Current page: ${document.title} (${window.location.pathname})`,
        ),
    },
    {
      name: "vibing_navigate",
      title: "Navigate this site",
      description:
        "Move the browser to another page on vibing.inc without a full reload. Accepts a site path such as /library, /services/vibe-coding-rescue or /library/some-gem-slug.",
      inputSchema: {
        type: "object",
        properties: {
          path: { type: "string", description: "Site-relative path starting with /" },
        },
        required: ["path"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false, openWorldHint: false },
      execute: (input) => {
        const path = String(input.path ?? "");
        if (!path.startsWith("/") || path.startsWith("//")) {
          return errorResult("path must be a site-relative path starting with a single /");
        }
        deps.navigate(path);
        return textResult(`Navigated to ${path}`);
      },
    },
    {
      name: "vibing_list_pages",
      title: "List site pages",
      description:
        "List every public page on vibing.inc with its title and summary — services, guides, the library and core pages.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, idempotentHint: true },
      execute: () =>
        jsonResult(
          {
            core: CORE_PAGES,
            services: SERVICE_PAGES,
            guides: GUIDE_PAGES,
          },
          `${ALL_PAGES.length} public pages`,
        ),
    },
    {
      name: "vibing_get_services",
      title: "Get services",
      description:
        "Get the services the Vibing Company offers, with page paths and summaries.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, idempotentHint: true },
      execute: () => jsonResult(SERVICE_PAGES, `${SERVICE_PAGES.length} services`),
    },
    {
      name: "vibing_get_company_facts",
      title: "Get company facts",
      description:
        "Get factual details about the Vibing Company: what it does, who runs it, location, contact email and booking link.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true, idempotentHint: true },
      execute: () => jsonResult(SITE, SITE.tagline),
    },
    {
      name: "vibing_book_a_call",
      title: "Open the booking calendar",
      description:
        "Open the Vibing Company booking calendar in a new tab so the user can pick a slot. Ask the user before calling this.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: false, openWorldHint: true },
      execute: () => {
        window.open(SITE.bookingUrl, "_blank", "noopener,noreferrer");
        return textResult(`Opened the booking calendar: ${SITE.bookingUrl}`);
      },
    },
    {
      name: "vibing_start_contact",
      title: "Start a contact request",
      description:
        "Take the user to the contact form, optionally pre-filling what they need help with. The user still reviews and submits the form themselves.",
      inputSchema: {
        type: "object",
        properties: {
          topic: { type: "string", description: "Short summary of what they need help with" },
        },
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false },
      execute: (input) => {
        const topic = typeof input.topic === "string" ? input.topic.slice(0, 400) : "";
        deps.navigate(topic ? `/contact?topic=${encodeURIComponent(topic)}` : "/contact");
        return textResult(
          "Opened the contact form. The user reviews and submits it — nothing was sent.",
        );
      },
    },
  ];
}
